import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  type ApiQueryParams,
  CatalogPropertiesService,
  type CatalogProperty,
  type CatalogPropertyGetResponse,
  type CatalogPropertyListResponse,
} from 'src/api/generated';
import { type ApiResponse, useMemoizedArrayData } from 'src/hooks/api/api';

/**
 * Parameters for the useProperties hook.
 * Extends ApiQueryParams with an optional enabled flag to control query execution.
 */
interface UsePropertiesParams extends ApiQueryParams {
  /** Whether the query is enabled. Defaults to true. Disable to prevent query execution. */
  enabled?: boolean;
}

/**
 * Response object returned by the useProperties hook.
 * Contains paginated property data and pagination controls.
 */
interface PropertiesResponse extends ApiResponse {
  /** Array of property items from the current pages. */
  properties: CatalogProperty[];
  /** Function to fetch the next page of results. */
  fetchNextPage: () => Promise<unknown>;
  /** Whether there are more pages available to fetch. */
  hasNextPage: boolean;
  /** Whether a next page is currently being fetched. */
  isFetchingNextPage: boolean;
  /** Token for fetching the next page of results. */
  nextPageToken?: string;
  /** Total count of properties matching the query filters. */
  totalCount?: number;
}

/**
 * Hook for fetching a paginated list of catalog properties.
 * Supports filtering, searching, sorting, and infinite pagination.
 *
 * @param params - Query parameters including filters, search, sort, pagination, and enabled flag
 * @returns Response object containing properties array, pagination state, and control functions
 */
export const useProperties = (
  params: UsePropertiesParams = {}
): PropertiesResponse => {
  const { enabled = true, ...queryParams } = params;

  const fetchProperties = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }): Promise<CatalogPropertyListResponse> => {
    const { filterBy, searchBy, sortBy, page, pageSize } = queryParams;

    return await CatalogPropertiesService.listProperties(
      filterBy,
      searchBy,
      sortBy,
      page,
      pageSize,
      pageParam
    );
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['catalog/properties', queryParams],
    queryFn: fetchProperties,
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
    initialPageParam: undefined,
    enabled,
  });

  const properties = useMemoizedArrayData<CatalogProperty>(
    data?.pages.flatMap((page) => page.items) ?? []
  );
  const totalCount = data?.pages[0]?.totalCount;

  return {
    loading,
    error,
    properties,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

/**
 * Response object returned by the useProperty hook.
 * Contains a single property detail or null if not loaded.
 */
interface UsePropertyResponse extends ApiResponse {
  /** The property detail object, or null if not yet loaded or not found. */
  property: CatalogProperty | null;
}

/**
 * Hook for fetching a single property by ID.
 * Returns the property detail with loading and error states.
 *
 * @param id - The unique identifier of the property to fetch
 * @param enabled - Whether the query is enabled. Set to false to prevent fetching.
 * @returns Response object containing property data, loading state, errors, and refetch function
 */
export const useProperty = (
  id: number,
  enabled: boolean
): UsePropertyResponse => {
  const fetchProperty = async (): Promise<CatalogPropertyGetResponse> => {
    return await CatalogPropertiesService.getProperty(id);
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['catalog/properties', 'detail', id],
    queryFn: fetchProperty,
    enabled,
  });

  return {
    loading,
    error,
    property: data ?? null,
    refetch,
  };
};
